import React from 'react';
import { Container, Grid, Box, Typography, Card } from '@mui/material';
import { motion } from 'framer-motion';
import CodeIcon from '@mui/icons-material/Code';
import CloudIcon from '@mui/icons-material/Cloud';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import SecurityIcon from '@mui/icons-material/Security';
import { useScrollAnimation, fadeInUp, staggerContainer, scaleIn } from '../hooks/useScrollAnimation';

const AboutSection = () => {
  const [titleRef, titleControls] = useScrollAnimation();
  const [profilesRef, profilesControls] = useScrollAnimation();
  const [competenciesRef, competenciesControls] = useScrollAnimation();
  const profiles = [
    {
      icon: <CodeIcon sx={{ fontSize: 40 }} />,
      title: 'Fullstack Development',
      description: 'Expert in C#, Java, JavaScript/TypeScript, Python with 10+ years building scalable web and mobile applications.',
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40 }} />,
      title: 'DevOps & CI/CD',
      description: 'Proficient in Docker, Kubernetes, Linux server administration, GitHub Actions, and automated deployment pipelines.',
    },
    {
      icon: <DesktopMacIcon sx={{ fontSize: 40 }} />,
      title: 'Desktop Applications',
      description: 'Very good experience building desktop applications with modern frameworks and native technologies.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'Enterprise Solutions',
      description: 'PLM Systems (Aras Corp, Teamcenter), WordPress, Joomla, and complex enterprise deployment solutions.',
    },
  ];

  return (
    <Box
      id="about"
      sx={{
        py: { xs: 10, md: 15 },
        background: 'rgba(0, 0, 0, 0.2)',
      }}
    >
      <Container maxWidth="xl">
        <Box 
          component={motion.div}
          ref={titleRef}
          initial="hidden"
          animate={titleControls}
          variants={fadeInUp}
          sx={{ mb: 8, textAlign: 'center' }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              mb: 2,
            }}
          >
            Professional Profile
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.8,
            }}
          >
            A seasoned software engineer with deep expertise in fullstack development, DevOps practices,
            and enterprise systems. Specialized in building robust, scalable solutions across web, mobile,
            and desktop platforms.
          </Typography>
        </Box>

        <Grid 
          container 
          spacing={4} 
          sx={{ mb: 8 }}
          component={motion.div}
          ref={profilesRef}
          initial="hidden"
          animate={profilesControls}
          variants={staggerContainer}
        >
          {profiles.map((profile, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Card
                component={motion.div}
                variants={scaleIn}
                whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
                sx={{
                  height: '100%',
                  background: 'var(--card-bg)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid var(--border-subtle)',
                  p: 4,
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: 'primary.main',
                    boxShadow: '0 20px 60px rgba(0, 245, 255, 0.2)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #00F5FF, #FF6B35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    color: '#0B0D17',
                  }}
                >
                  {profile.icon}
                </Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  {profile.title}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {profile.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Key Specializations */}
        <Box 
          component={motion.div}
          ref={competenciesRef}
          initial="hidden"
          animate={competenciesControls}
          variants={fadeInUp}
          sx={{ textAlign: 'center', mt: 10 }}
        >
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
            Core Competencies
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                title: 'Programming Languages',
                skills: ['C#', 'Java', 'JavaScript', 'TypeScript', 'Python', 'PHP'],
              },
              {
                title: 'Frameworks & Libraries',
                skills: ['React', 'Node.js', 'ASP.NET', 'Spring Boot', 'Django', 'React Native'],
              },
              {
                title: 'DevOps & Infrastructure',
                skills: ['Docker', 'Kubernetes', 'Linux Admin', 'CI/CD', 'GitHub Actions', 'Jenkins'],
              },
              {
                title: 'Enterprise & CMS',
                skills: ['Aras PLM', 'Teamcenter', 'WordPress', 'Joomla', 'Plugin Development'],
              },
              {
                title: 'Cloud & Databases',
                skills: ['AWS', 'Azure', 'PostgreSQL', 'MongoDB', 'Redis', 'MySQL'],
              },
              {
                title: 'Specialized Services',
                skills: ['XRechnung', 'ZUGFeRD', 'Complex Deployments', 'Desktop Apps', 'Product Management'],
              },
            ].map((category, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Card
                  sx={{
                    background: 'var(--card-bg)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border-subtle)',
                    p: 3,
                    textAlign: 'left',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    {category.title}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {category.skills.map((skill, skillIdx) => (
                      <Box
                        key={skillIdx}
                        sx={{
                          px: 2,
                          py: 1,
                          background: 'rgba(0, 245, 255, 0.1)',
                          border: '1px solid rgba(0, 245, 255, 0.2)',
                          borderRadius: 2,
                          fontSize: '0.85rem',
                          fontFamily: "'Fira Code', monospace",
                          color: 'text.primary',
                        }}
                      >
                        {skill}
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutSection;
